import React, { useState, useEffect, useCallback, useRef } from 'react';
import { sourcesAPI } from '../services/api';
import { Button } from '../components/Common/Button';
import { Card } from '../components/Common/Card';
import { Select } from '../components/Common/Select';
import { Input } from '../components/Common/Input';
import { Label } from '../components/Common/Label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/Common/Dialog';
import { Database, Plus, Edit, Trash2, ExternalLink, DownloadCloud } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import {
  SWISS_REGIONS,
  REGION_LABELS,
  SOURCE_TYPES,
  SOURCE_TYPE_LABELS,
  PERMISSION_STATUSES,
  PERMISSION_STATUS_MAP,
} from '../constants/regions';

// Leeres Formular — Status ist beim Anlegen mit "unclear" vorausgefüllt.
const emptyForm = {
  name: '',
  url: '',
  region: '',
  source_type: '',
  permission_status: 'unclear',
  contact_person: '',
  contact_email: '',
  last_reviewed: '',
  notes: '',
};

const StatusBadge = ({ status }) => {
  const s = PERMISSION_STATUS_MAP[status] || PERMISSION_STATUS_MAP.unclear;
  return <span className={`status-badge ${s.badgeClass}`}>{s.label}</span>;
};

const Quellen = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filter (reine Dropdown-Auswahl, keine Suchsyntax)
  const [filterRegion, setFilterRegion] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Formular: null = geschlossen, sonst { id? , ...felder }
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  // Löschen
  const [toDelete, setToDelete] = useState(null);

  // Import aus Aktivitäten: Vorschau-Panel
  const [importOpen, setImportOpen] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [selectedUrls, setSelectedUrls] = useState(() => new Set());
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState('');

  // Paginierung (wie auf der Moderationsseite: 20 Einträge pro Seite)
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Ref zum Formular, um beim Bearbeiten dorthin zu scrollen
  const formRef = useRef(null);

  const loadSources = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (filterRegion) params.region = filterRegion;
      if (filterStatus) params.permission_status = filterStatus;
      const res = await sourcesAPI.getAll(params);
      setSources(res.data);
    } catch (e) {
      console.error('Fehler beim Laden der Quellen:', e);
      setError('Die Quellen konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [filterRegion, filterStatus]);

  useEffect(() => {
    loadSources();
  }, [loadSources]);

  // Beim Bearbeiten zum Formular scrollen und erstes Feld fokussieren
  useEffect(() => {
    if (form && form.id && formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setTimeout(() => {
        const firstInput = formRef.current?.querySelector('input, textarea, select');
        if (firstInput) firstInput.focus();
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form?.id]);

  // Bei Filterwechsel zurück auf Seite 1
  useEffect(() => {
    setCurrentPage(1);
  }, [filterRegion, filterStatus]);

  // Aktuelle Seite begrenzen, wenn die Liste kürzer wird (z.B. nach Löschen)
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(sources.length / itemsPerPage));
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [sources, currentPage]);

  const openNew = () => {
    setFormError('');
    setForm({ ...emptyForm });
  };

  const openEdit = (source) => {
    setFormError('');
    setForm({
      id: source.id,
      name: source.name || '',
      url: source.url || '',
      region: source.region || '',
      source_type: source.source_type || '',
      permission_status: source.permission_status || 'unclear',
      contact_person: source.contact_person || '',
      contact_email: source.contact_email || '',
      last_reviewed: source.last_reviewed || '',
      notes: source.notes || '',
    });
  };

  const closeForm = () => {
    setForm(null);
    setFormError('');
  };

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSave = async () => {
    // Pflichtfelder client-seitig prüfen (Backend prüft ebenfalls)
    if (!form.name.trim() || !form.url.trim() || !form.region || !form.source_type) {
      setFormError('Bitte Name, Adresse (URL), Region und Typ ausfüllen.');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      const payload = {
        name: form.name.trim(),
        url: form.url.trim(),
        region: form.region,
        source_type: form.source_type,
        permission_status: form.permission_status,
        contact_person: form.contact_person.trim(),
        contact_email: form.contact_email.trim(),
        last_reviewed: form.last_reviewed || null,
        notes: form.notes.trim(),
      };
      if (form.id) {
        await sourcesAPI.update(form.id, payload);
      } else {
        await sourcesAPI.create(payload);
      }
      closeForm();
      await loadSources();
    } catch (e) {
      console.error('Fehler beim Speichern der Quelle:', e);
      setFormError(e.response?.data?.error || 'Die Quelle konnte nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!toDelete) return;
    try {
      await sourcesAPI.delete(toDelete.id);
      setToDelete(null);
      await loadSources();
    } catch (e) {
      console.error('Fehler beim Löschen der Quelle:', e);
      setError('Die Quelle konnte nicht gelöscht werden.');
      setToDelete(null);
    }
  };

  const openImport = async () => {
    setImportOpen(true);
    setImportError('');
    setImportResult('');
    setImportLoading(true);
    setCandidates([]);
    try {
      const res = await sourcesAPI.importPreview();
      const list = res.data || [];
      setCandidates(list);
      // Alle Vorschläge vorausgewählt
      setSelectedUrls(new Set(list.map((c) => c._candidate_key)));
    } catch (e) {
      console.error('Fehler bei der Import-Vorschau:', e);
      setImportError(
        e.response?.data?.error ||
          'Die Vorschau konnte nicht erstellt werden. Bitte später erneut versuchen.'
      );
    } finally {
      setImportLoading(false);
    }
  };

  const closeImport = () => {
    setImportOpen(false);
    setCandidates([]);
    setSelectedUrls(new Set());
    setImportError('');
  };

  const toggleCandidate = (key) => {
    setSelectedUrls((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const allSelected = candidates.length > 0 && selectedUrls.size === candidates.length;
  const toggleAll = () => {
    setSelectedUrls(allSelected ? new Set() : new Set(candidates.map((c) => c._candidate_key)));
  };

  const handleImportConfirm = async () => {
    const chosen = candidates.filter((c) => selectedUrls.has(c._candidate_key));
    if (chosen.length === 0) return;
    setImporting(true);
    setImportError('');
    try {
      const res = await sourcesAPI.importConfirm(chosen);
      const { created = 0, skipped = 0 } = res.data || {};
      setImportResult(
        `${created} Quelle(n) übernommen` +
          (skipped ? `, ${skipped} übersprungen (bereits vorhanden).` : '.')
      );
      closeImport();
      await loadSources();
    } catch (e) {
      console.error('Fehler beim Übernehmen der Quellen:', e);
      setImportError(
        e.response?.data?.error || 'Die Auswahl konnte nicht übernommen werden.'
      );
    } finally {
      setImporting(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '–';
    const [y, m, d] = iso.split('-');
    return `${d}.${m}.${y}`;
  };

  // Paginierung auf die geladene (bereits gefilterte) Liste anwenden
  const totalPages = Math.max(1, Math.ceil(sources.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedSources = sources.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className="quellen-page">
      <h1 className="text-2xl sm:text-3xl font-bold mb-2">Quellen</h1>
      <p className="text-sm sm:text-base text-gray-600 mb-6">
        Webseiten, auf denen Aktivitäten für Grosskinder publiziert werden – nach Region,
        Kontaktperson und Bewilligung.
      </p>

      {error && <div className="error-message mb-4">{error}</div>}

      {/* Filterzeile + Erfassen-Button */}
      <Card className="p-4 mb-4">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <Label htmlFor="filter-region">Region</Label>
            <div className="mt-1">
              <Select id="filter-region" value={filterRegion} onValueChange={setFilterRegion}>
                <option value="">Alle Regionen</option>
                {SWISS_REGIONS.map((r) => (
                  <option key={r.code} value={r.code}>{r.label}</option>
                ))}
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="filter-status">Bewilligung</Label>
            <div className="mt-1">
              <Select id="filter-status" value={filterStatus} onValueChange={setFilterStatus}>
                <option value="">Alle Status</option>
                {PERMISSION_STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </Select>
            </div>
          </div>

          <div className="ml-auto flex flex-wrap gap-2">
            <Button variant="outline" onClick={openImport} disabled={importOpen}>
              <DownloadCloud size={16} /> Aus Aktivitäten importieren
            </Button>
            <Button onClick={openNew}>
              <Plus size={16} /> Neue Quelle erfassen
            </Button>
          </div>
        </div>
      </Card>

      {importResult && <div className="success-message mb-4">{importResult}</div>}

      {/* Import-Vorschau (inline, aufklappend) */}
      {importOpen && (
        <Card className="p-6 mb-4">
          <h2 className="text-xl font-semibold mb-2">Aus Aktivitäten importieren</h2>
          <p className="text-sm text-gray-600 mb-4">
            Vorschläge aus bereits erfassten Aktivitäten. Prüfen Sie die Auswahl und übernehmen
            Sie die gewünschten Quellen — nichts wird ohne Ihre Bestätigung gespeichert.
          </p>

          {importError && <div className="error-message mb-4">{importError}</div>}

          {importLoading ? (
            <div>Vorschläge werden ermittelt … (das kann einen Moment dauern)</div>
          ) : candidates.length === 0 && !importError ? (
            <>
              <p className="text-gray-600">
                Keine neuen Quellen gefunden — alle erkannten Domains sind bereits erfasst.
              </p>
              <div className="dialog-footer mt-4">
                <Button variant="outline" onClick={closeImport}>Schliessen</Button>
              </div>
            </>
          ) : candidates.length > 0 ? (
            <>
              <label className="flex items-center gap-2 mb-3 text-sm font-medium" style={{ cursor: 'pointer' }}>
                <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                Alle auswählen ({selectedUrls.size}/{candidates.length})
              </label>

              {/* Desktop-Tabelle */}
              <table className="activities-table activities-table-desktop">
                <thead>
                  <tr>
                    <th></th>
                    <th>Name</th>
                    <th>Domain</th>
                    <th>Region</th>
                    <th>Typ</th>
                    <th>Aktivitäten</th>
                    <th>Hinweis</th>
                  </tr>
                </thead>
                <tbody>
                  {candidates.map((c) => {
                    const domainUrl = c.url || c.aggregator_domain;
                    return (
                      <tr key={c._candidate_key}>
                        <td>
                          <input type="checkbox" checked={selectedUrls.has(c._candidate_key)}
                            onChange={() => toggleCandidate(c._candidate_key)} />
                        </td>
                        <td>{c.name}</td>
                        <td>
                          {domainUrl ? (
                            <a href={domainUrl} target="_blank" rel="noopener noreferrer" className="source-link">
                              {domainUrl}
                            </a>
                          ) : '—'}
                          {c.aggregator_domain && !c.url && (
                            <span className="text-xs text-gray-500 block">via Aggregator</span>
                          )}
                        </td>
                        <td>{c.region_label || '—'}</td>
                        <td>{SOURCE_TYPE_LABELS[c.source_type] || c.source_type}</td>
                        <td>{c.activity_count}</td>
                        <td className="text-xs text-gray-600">{c.notes}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Mobile-Karten */}
              <div className="activities-cards-mobile space-y-3">
                {candidates.map((c) => {
                  const domainUrl = c.url || c.aggregator_domain;
                  return (
                    <Card key={c._candidate_key} className="p-3" style={{ minHeight: 'auto' }}>
                      <label className="flex items-start gap-2" style={{ cursor: 'pointer' }}>
                        <input type="checkbox" checked={selectedUrls.has(c._candidate_key)}
                          onChange={() => toggleCandidate(c._candidate_key)} style={{ marginTop: '0.25rem' }} />
                        <div className="space-y-1">
                          <div className="font-medium">{c.name}</div>
                          {domainUrl && (
                            <a href={domainUrl} target="_blank" rel="noopener noreferrer" className="source-link"
                              style={{ fontSize: '0.75rem' }}>
                              {domainUrl}{c.aggregator_domain && !c.url ? ' (Aggregator)' : ''}
                            </a>
                          )}
                          <div className="text-xs text-gray-600">
                            {(c.region_label || 'ohne Region')} • {SOURCE_TYPE_LABELS[c.source_type] || c.source_type} • {c.activity_count} Aktivitäten
                          </div>
                          <div className="text-xs text-gray-500">{c.notes}</div>
                        </div>
                      </label>
                    </Card>
                  );
                })}
              </div>

              <div className="dialog-footer mt-6">
                <Button variant="outline" onClick={closeImport} disabled={importing}>Abbrechen</Button>
                <Button onClick={handleImportConfirm} disabled={importing || selectedUrls.size === 0}>
                  {importing ? 'Übernehmen…' : `Ausgewählte übernehmen (${selectedUrls.size})`}
                </Button>
              </div>
            </>
          ) : (
            <div className="dialog-footer mt-4">
              <Button variant="outline" onClick={closeImport}>Schliessen</Button>
            </div>
          )}
        </Card>
      )}

      {/* Erfassungs-/Bearbeitungsformular (inline, aufklappend) */}
      {form && (
        <div ref={formRef}>
        <Card className="p-6 mb-4">
          <h2 className="text-xl font-semibold mb-4">
            {form.id ? 'Quelle bearbeiten' : 'Neue Quelle erfassen'}
          </h2>

          {formError && <div className="error-message mb-4">{formError}</div>}

          {/* Pflichtangaben zuerst */}
          <p className="text-sm font-semibold text-gray-700 mb-2">Pflichtangaben</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <div>
              <Label htmlFor="f-name">Name *</Label>
              <div className="mt-1">
                <Input id="f-name" value={form.name} onChange={(e) => setField('name', e.target.value)}
                  placeholder="z.B. Kikuka Winterthur" required />
              </div>
            </div>
            <div>
              <Label htmlFor="f-url">Adresse (URL) *</Label>
              <div className="mt-1">
                <Input id="f-url" type="url" value={form.url} onChange={(e) => setField('url', e.target.value)}
                  placeholder="https://…" required />
              </div>
            </div>
            <div>
              <Label htmlFor="f-region">Region *</Label>
              <div className="mt-1">
                <Select id="f-region" value={form.region} onValueChange={(v) => setField('region', v)} required>
                  <option value="" disabled>Bitte wählen…</option>
                  {SWISS_REGIONS.map((r) => (
                    <option key={r.code} value={r.code}>{r.label}</option>
                  ))}
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="f-type">Typ *</Label>
              <div className="mt-1">
                <Select id="f-type" value={form.source_type} onValueChange={(v) => setField('source_type', v)} required>
                  <option value="" disabled>Bitte wählen…</option>
                  {SOURCE_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </Select>
              </div>
            </div>
          </div>

          {/* Weitere Angaben – optional */}
          <p className="text-sm font-semibold text-gray-700 mb-2">Weitere Angaben (optional)</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="f-contact">Kontaktperson</Label>
              <div className="mt-1">
                <Input id="f-contact" value={form.contact_person}
                  onChange={(e) => setField('contact_person', e.target.value)} placeholder="Vor- und Nachname" />
              </div>
            </div>
            <div>
              <Label htmlFor="f-email">E-Mail Kontaktperson</Label>
              <div className="mt-1">
                <Input id="f-email" type="email" value={form.contact_email}
                  onChange={(e) => setField('contact_email', e.target.value)} placeholder="name@example.ch" />
              </div>
            </div>
            <div>
              <Label htmlFor="f-permission">Bewilligung</Label>
              <div className="mt-1">
                <Select id="f-permission" value={form.permission_status}
                  onValueChange={(v) => setField('permission_status', v)}>
                  {PERMISSION_STATUSES.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="f-reviewed">Letzte Prüfung</Label>
              <div className="mt-1">
                <Input id="f-reviewed" type="date" value={form.last_reviewed}
                  onChange={(e) => setField('last_reviewed', e.target.value)} />
              </div>
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="f-notes">Notizen</Label>
              <div className="mt-1">
                <textarea id="f-notes" className="input" rows={3} value={form.notes}
                  onChange={(e) => setField('notes', e.target.value)}
                  placeholder="z.B. ruft quartalsweise an, PDF-Flyer statt HTML …" />
              </div>
            </div>
          </div>

          <div className="dialog-footer mt-6">
            <Button variant="outline" onClick={closeForm} disabled={saving}>Abbrechen</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Speichern…' : (form.id ? 'Änderungen speichern' : 'Quelle speichern')}
            </Button>
          </div>
        </Card>
        </div>
      )}

      {/* Liste */}
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-6">
          <Database size={20} className="text-blue-600" />
          <h2 className="text-xl font-semibold">Erfasste Quellen ({sources.length})</h2>
        </div>

        {loading ? (
          <div>Lädt…</div>
        ) : sources.length === 0 ? (
          <p className="text-gray-600">Noch keine Quellen erfasst. Mit „Neue Quelle erfassen" beginnen.</p>
        ) : (
          <>
            {/* Desktop-Tabelle */}
            <table className="activities-table activities-table-desktop">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Domain</th>
                  <th>Region</th>
                  <th>Typ</th>
                  <th>Bewilligung</th>
                  <th>Kontaktperson</th>
                  <th>Letzte Prüfung</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {paginatedSources.map((s) => (
                  <tr key={s.id}>
                    <td>{s.name}</td>
                    <td>
                      {s.url ? (
                        <a href={s.url} target="_blank" rel="noopener noreferrer"
                          className="source-link" title={s.url}>
                          {s.url} <ExternalLink size={13} style={{ display: 'inline', verticalAlign: 'middle' }} />
                        </a>
                      ) : '—'}
                    </td>
                    <td>{s.region_label || REGION_LABELS[s.region] || s.region}</td>
                    <td>{SOURCE_TYPE_LABELS[s.source_type] || s.source_type}</td>
                    <td><StatusBadge status={s.permission_status} /></td>
                    <td>{s.contact_person || '–'}</td>
                    <td>{formatDate(s.last_reviewed)}</td>
                    <td className="action-buttons">
                      <Button variant="outline" onClick={() => openEdit(s)} className="btn-sm btn-edit">
                        <Edit size={16} />
                      </Button>
                      {isAdmin && (
                        <Button variant="outline" onClick={() => setToDelete(s)} className="btn-sm btn-delete">
                          <Trash2 size={16} />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile-Karten */}
            <div className="activities-cards-mobile space-y-3">
              {paginatedSources.map((s) => (
                <Card key={s.id} className="p-3" style={{ minHeight: 'auto' }}>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <div className="font-medium">{s.name}</div>
                        {s.url && (
                          <a href={s.url} target="_blank" rel="noopener noreferrer"
                            className="source-link" title={s.url} style={{ fontSize: '0.75rem' }}>
                            {s.url} <ExternalLink size={12} style={{ display: 'inline', verticalAlign: 'middle' }} />
                          </a>
                        )}
                      </div>
                      <StatusBadge status={s.permission_status} />
                    </div>
                    <div className="flex gap-2 text-xs text-gray-600" style={{ flexWrap: 'wrap' }}>
                      <span>{s.region_label || REGION_LABELS[s.region] || s.region}</span>
                      <span>•</span>
                      <span>{SOURCE_TYPE_LABELS[s.source_type] || s.source_type}</span>
                      {s.contact_person && (<><span>•</span><span>{s.contact_person}</span></>)}
                    </div>
                    <div className="flex gap-2 items-center pt-2 border-t justify-between">
                      <span className="text-xs text-gray-500">Geprüft: {formatDate(s.last_reviewed)}</span>
                      <div className="flex gap-2">
                        <Button variant="outline" onClick={() => openEdit(s)} className="btn-sm btn-edit"
                          style={{ padding: 'var(--spacing-1) var(--spacing-2)' }}>
                          <Edit size={14} />
                        </Button>
                        {isAdmin && (
                          <Button variant="outline" onClick={() => setToDelete(s)} className="btn-sm btn-delete"
                            style={{ padding: 'var(--spacing-1) var(--spacing-2)' }}>
                            <Trash2 size={14} />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '10px',
                marginTop: '20px',
                padding: '15px 0',
              }}>
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  style={{ padding: '8px 16px', opacity: currentPage === 1 ? 0.5 : 1 }}
                >
                  Zurück
                </Button>

                <span style={{ fontSize: '14px', color: '#6c757d' }}>
                  Seite {currentPage} von {totalPages} ({sources.length} Einträge)
                </span>

                <Button
                  variant="outline"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  style={{ padding: '8px 16px', opacity: currentPage === totalPages ? 0.5 : 1 }}
                >
                  Weiter
                </Button>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Löschen bestätigen */}
      {toDelete && (
        <Dialog open={!!toDelete} onOpenChange={(open) => !open && setToDelete(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Quelle löschen</DialogTitle>
              <DialogDescription>
                Möchten Sie die Quelle „{toDelete.name}" wirklich löschen? Diese Aktion kann
                nicht rückgängig gemacht werden.
              </DialogDescription>
            </DialogHeader>
            <div className="dialog-footer">
              <Button variant="outline" onClick={() => setToDelete(null)}>Abbrechen</Button>
              <Button className="btn-delete-confirm" onClick={handleConfirmDelete}>Löschen</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default Quellen;
